import { useLazyQuery, useMutation } from "@apollo/client";
import { clientNeoFact } from "@neonumy/api-clients";
import { useMemo, useEffect } from "react";
import { onErrorGraphql } from "utils/onErrorGraphql";
import {
  ARCHIVE_PRODUCT,
  CREATE_PRODUCT,
  DELETE_PRODUCT,
  GET_PRODUCT,
  LIST_CATEGORY,
  LIST_PRODUCTS,
  LIST_PRODUCTS_TYPES,
  LIST_PRODUCT_OPERATIONAL_CATEGORIES,
  REACTIVATE_PRODUCT,
  UPDATE_PRODUCT,
  UPDATE_PRODUCT_BUY_PRICE,
  UPDATE_PRODUCT_PRICE,
} from "queries/neoFact/catalog";
import { language } from "config/settings";

export default function useProductApi(id?: ID) {
  const [listProducts, { refetch, loading, data: listProductsData, error: listProductsError }] =
    useLazyQuery<ListProductsResponse, ListProductsVars>(LIST_PRODUCTS, {
      client: clientNeoFact,
      fetchPolicy: "no-cache",
    });

  useEffect(() => {
    if (listProductsError) onErrorGraphql(listProductsError);
  }, [listProductsError]);

  const dataSource =
    listProductsData?.listProducts?.results?.map(item => ({
      ...item,
      key: item.id,
      name: item.nameTranslations?.find(t => t.language === language)?.value,
      description: item.descriptionTranslations?.find(t => t.language === language)?.value,
    })) ?? [];

  const totalCount = listProductsData?.listProducts?.totalCount ?? 0;

  const [listProductTypes, { data: listProductTypesData, error: listProductTypesError }] =
    useLazyQuery<ListProductTypesResponse, ListProductTypesVars>(LIST_PRODUCTS_TYPES, {
      client: clientNeoFact,
      fetchPolicy: "no-cache",
    });

  useEffect(() => {
    if (listProductTypesError) onErrorGraphql(listProductTypesError);
  }, [listProductTypesError]);

  const productTypesOptions = useMemo(
    () =>
      listProductTypesData?.listProductTypes?.results?.map(item => ({
        ...item,
        key: item.id,
      })) ?? [],
    [listProductTypesData],
  );

  const [
    getProduct,
    {
      refetch: refetchGetProduct,
      loading: getProductLoading,
      data: getProductData,
      error: getProductError,
    },
  ] = useLazyQuery<GetProductResponse, GetVarTypeID>(GET_PRODUCT, {
    client: clientNeoFact,
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (getProductError) onErrorGraphql(getProductError);
  }, [getProductError]);

  const formData = getProductData?.getProduct;

  const [
    listCategories,
    { refetch: refetchCategories, data: listCategoriesData, error: listCategoriesError },
  ] = useLazyQuery<ListCategoriesResponse, ListCategoriesVars>(LIST_CATEGORY, {
    client: clientNeoFact,
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (listCategoriesError) onErrorGraphql(listCategoriesError);
  }, [listCategoriesError]);

  const categoryOptions = useMemo(
    () =>
      listCategoriesData?.listCategories?.results?.map((item: ListCategoriesResult) => ({
        key: item.id,
        label: item.name,
        value: item.id,
      })) ?? [],
    [listCategoriesData],
  );

  const [
    listProductOperationalCategories,
    { data: listProductOperationalCategoriesData, error: listProductOperationalCategoriesError },
  ] = useLazyQuery<ListProductOperationalCategoriesResponse>(LIST_PRODUCT_OPERATIONAL_CATEGORIES, {
    client: clientNeoFact,
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
  });

  if (listProductOperationalCategoriesError) {
    onErrorGraphql(listProductOperationalCategoriesError);
  }

  const operationsCategories = useMemo(
    () =>
      listProductOperationalCategoriesData?.listProductOperationalCategories?.map(
        (item: { label: string; value: string }) => ({
          label: item.label,
          value: item.value,
        }),
      ) ?? [],
    [listProductOperationalCategoriesData],
  );

  const [
    createUpdateProduct,
    { loading: createUpdateProductLoading, error: createUpdateProductError },
  ] = useMutation<CreateUpdateProductMutationResponse, CreateProductVars | UpdateProductVars>(
    id ? UPDATE_PRODUCT : CREATE_PRODUCT,
    {
      client: clientNeoFact,
      fetchPolicy: "no-cache",
    },
  );

  useEffect(() => {
    if (createUpdateProductError) onErrorGraphql(createUpdateProductError);
  }, [createUpdateProductError]);

  const [updateProductBuyPrice, { error: updateProductBuyPriceError }] = useMutation<
    UpdateProductBuyPriceMutationResponse,
    UpdateProductBuyPriceVars
  >(UPDATE_PRODUCT_BUY_PRICE, {
    client: clientNeoFact,
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (updateProductBuyPriceError) onErrorGraphql(updateProductBuyPriceError);
  }, [updateProductBuyPriceError]);

  const [updateProductPrice, { error: updateProductPriceError }] = useMutation<
    UpdateProductPriceMutationResponse,
    UpdateProductPriceVars
  >(UPDATE_PRODUCT_PRICE, {
    client: clientNeoFact,
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (updateProductPriceError) onErrorGraphql(updateProductPriceError);
  }, [updateProductPriceError]);

  const [deleteProduct, { loading: deleteProductLoading, error: deleteProductError }] = useMutation<
    DeleteProductResponse,
    GetVarTypeID
  >(DELETE_PRODUCT, {
    client: clientNeoFact,
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (deleteProductError) onErrorGraphql(deleteProductError);
  }, [deleteProductError]);

  const [archiveProduct, { loading: archiveProductLoading, error: archiveProductError }] =
    useMutation<ArchiveProductResponse, GetVarTypeID>(ARCHIVE_PRODUCT, {
      client: clientNeoFact,
      fetchPolicy: "no-cache",
    });

  useEffect(() => {
    if (archiveProductError) onErrorGraphql(archiveProductError);
  }, [archiveProductError]);

  const [reactivateProduct, { loading: reactivateProductLoading, error: reactivateProductError }] =
    useMutation<ReactivateProductResponse, GetVarTypeID>(REACTIVATE_PRODUCT, {
      client: clientNeoFact,
      fetchPolicy: "no-cache",
    });

  useEffect(() => {
    if (reactivateProductError) onErrorGraphql(reactivateProductError);
  }, [reactivateProductError]);

  return {
    refetch,
    loading,
    formData,
    dataSource,
    getProduct,
    totalCount,
    listProducts,
    deleteProduct,
    listCategories,
    archiveProduct,
    categoryOptions,
    refetchCategories,
    refetchGetProduct,
    listProductTypes,
    getProductLoading,
    reactivateProduct,
    updateProductPrice,
    createUpdateProduct,
    productTypesOptions,
    deleteProductLoading,
    operationsCategories,
    archiveProductLoading,
    updateProductBuyPrice,
    reactivateProductLoading,
    createUpdateProductLoading,
    listProductOperationalCategories,
  };
}
